import { type Static, Type } from "@sinclair/typebox";
import { CustomProbabilisticIdentifierNameSchema } from "./identifier-names";
import { Threshold } from "./thresholds";

const StaticMatcherSchema = Type.Object({
  type: Type.Literal("static"),
  weights: Type.Optional(
    Type.Record(Type.String(), Type.Number({ minimum: 0 })),
  ),
});

const ManualMatcherSchema = Type.Object({
  type: Type.Literal("manual"),
  edges: Type.Array(
    Type.Union([
      Type.Tuple([Type.String(), Type.String()]),
      Type.Tuple([Type.String(), Type.String(), Type.Number()]),
    ]),
  ),
});

const SnowflakeMLMatcherSchema = Type.Object({
  type: Type.Literal("snowflake_ml"),
  model: Type.String({ minLength: 1 }),
  version: Type.Optional(Type.String()),
});

const UdfMatcherSchema = Type.Object({
  type: Type.Literal("udf"),
  name: Type.String({
    minLength: 1,
    pattern: "^[A-Za-z0-9_]+$",
  }), // Snowflake UDF function name only — DB/schema come from workspace context
});

export const CustomProbabilisticIdentifierSchema = Type.Object({
  identifier: CustomProbabilisticIdentifierNameSchema,
  mismatch: Type.Union([Type.Literal("negative"), Type.Literal("neutral")]),
  blocking: Type.Boolean(),
  staticWeight: Type.Optional(Type.Integer({ exclusiveMinimum: 0 })),
});

const ThresholdSchema = Type.Object({
  threshold: Type.Enum(Threshold),
  value: Type.Optional(Type.Number({ exclusiveMinimum: 0, maximum: 1.0 })),
});

const TrainingSchema = Type.Object({
  model: Type.Optional(
    Type.Partial(
      Type.Object({
        name: Type.String({ minLength: 1 }),
        version: Type.String({ minLength: 1 }),
        testSize: Type.Number({ minimum: 0, maximum: 1 }),
        minF1: Type.Number({ minimum: 0, maximum: 1 }),
        minLabelCount: Type.Number({ minimum: 0 }),
        minPositiveRatio: Type.Number({ minimum: 0, maximum: 1 }),
        maxPositiveRatio: Type.Number({ minimum: 0, maximum: 1 }),
        params: Type.Record(Type.String(), Type.Any()),
      }),
    ),
  ),
  blocking: Type.Optional(
    Type.Union([Type.Boolean(), Type.Array(Type.String())]),
  ),
  truthLabelColumn: Type.Optional(Type.String()),
});

export const MatcherSchema = Type.Union([
  StaticMatcherSchema,
  ManualMatcherSchema,
  SnowflakeMLMatcherSchema,
  UdfMatcherSchema,
]);

export const ProbabilisticConfigSchema = Type.Object({
  enabled: Type.Boolean(),
  thresholds: Type.Array(ThresholdSchema),
  matcher: Type.Optional(MatcherSchema),
  blocking: Type.Optional(
    Type.Union([Type.Boolean(), Type.Array(Type.String())]),
  ),
  customIdentifiers: Type.Optional(
    Type.Array(CustomProbabilisticIdentifierSchema),
  ),
  training: Type.Optional(TrainingSchema),
});

// idr.probabilistic_config::JSONB
export type ProbabilisticConfig = Static<typeof ProbabilisticConfigSchema>;
export type CustomProbabilisticIdentifier = Static<
  typeof CustomProbabilisticIdentifierSchema
>;
export type MatcherConfig = NonNullable<ProbabilisticConfig["matcher"]>;
